import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

import { toFormikValidate } from "@thalamos/common";
import type {
  UpdateUserProfileRequest,
  UpdateUserProfileRequestUnbranded,
  User,
} from "@thalamos/haven-common";
import { UpdateUserProfileRequestSchema } from "@thalamos/haven-common";

/**
 * Whether the change landed.
 *
 * Named rather than a boolean because the caller has to render the difference,
 * and a bare `false` at a call site does not say what failed. One `failed`
 * covers both a server that would not accept the change and one that could not
 * be reached: nothing the person in front of the form could do differently
 * distinguishes them.
 */
export type ProfileSaveOutcome = "saved" | "failed";

/**
 * What the form edits, which is exactly what the endpoint accepts.
 *
 * Deriving it from the request type rather than restating the fields is what
 * keeps the two in step: a field added to the endpoint appears here as a
 * compile error at `initialValues` rather than as a form that silently edits
 * less than it could.
 */
export type UserProfileValues = UpdateUserProfileRequestUnbranded;

const validateProfile = toFormikValidate(UpdateUserProfileRequestSchema);

/**
 * The one form for an account's own details, used both by the person it belongs
 * to and by staff maintaining somebody else's.
 *
 * One component because it is one thing being edited. The difference between the
 * two cases is who may do it — which the server decides — and what the page
 * around it says, so the page supplies the heading and the save. Two forms would
 * have to be kept in step with each other and with the endpoint, and the one
 * that got less use would be the one that drifted.
 *
 * `onSave` is injected rather than the component reaching for the API client, so
 * mounting it needs neither a session nor a router: a story or a test can show
 * any state it has, including a save that failed.
 *
 * Validation is `UpdateUserProfileRequestSchema` — the same schema the server
 * refuses the write with. The field cannot then say a value is acceptable that
 * the endpoint will reject, which is the failure mode of a form that carries its
 * own idea of what is valid.
 */
export const UserProfileForm = ({
  user,
  onSave,
}: {
  user: User;
  onSave: (request: UpdateUserProfileRequest) => Promise<ProfileSaveOutcome>;
}) => {
  const { t } = useTranslation();

  return (
    <Formik<UserProfileValues>
      initialValues={{ name: user.name }}
      // The form is re-seeded when the account it is editing changes, which is
      // what stops a name typed against one account being carried into the next
      // — the administrative pages reuse this component across accounts.
      enableReinitialize
      validate={validateProfile}
      onSubmit={async (values, helpers) => {
        const request = UpdateUserProfileRequestSchema.safeParse(values);

        // Unreachable: Formik calls onSubmit only once `validateProfile` has
        // passed the same values against the same schema. Kept so a drift between
        // them shows the field errors rather than sending.
        /* v8 ignore if */
        if (!request.success) {
          helpers.setErrors((await validateProfile(values)) ?? {});
          return;
        }

        // A rejection is caught here rather than left to Formik, which reports
        // one to the console and clears `isSubmitting` — leaving somebody
        // looking at a form that did nothing and said nothing.
        const outcome = await onSave(request.data).catch(
          (): ProfileSaveOutcome => "failed",
        );

        // `status` rather than a field error: a failure is about the request
        // rather than about the value, and Formik clears field errors on the
        // next keystroke — which would make it disappear as soon as the user
        // touched anything.
        helpers.setStatus(outcome === "failed" ? "failed" : undefined);

        // Re-seeded from what was accepted, so the form is no longer dirty and
        // the button settles. A saved form that still looks unsaved invites the
        // same submission twice.
        if (outcome === "saved") {
          helpers.resetForm({ values });
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        dirty,
        isSubmitting,
        handleChange,
        handleBlur,
      }) => (
        <Form noValidate>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={3}>
              <TextField
                id="profile-name"
                name="name"
                label={t("profile.name.label")}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                // Shown only once the field has been left, so a message does
                // not appear while somebody is still typing the first letter of
                // their name.
                error={touched.name === true && errors.name !== undefined}
                // The schema's messages are translation keys — see
                // `UserNameSchema` — so what comes back is translated rather
                // than rendered.
                helperText={
                  touched.name === true && errors.name !== undefined
                    ? t(errors.name)
                    : t("profile.name.help")
                }
                fullWidth
              />

              {/* Shown because the address identifies the account and its
                  absence would read as an omission, and disabled because it is
                  the identity the provider vouched for — Haven has no operation
                  that changes one. */}
              <TextField
                id="profile-email"
                label={t("profile.email.label")}
                value={user.email}
                helperText={t("profile.email.help")}
                disabled
                fullWidth
              />

              {status === "failed" ? (
                <Alert severity="error">{t("errors.profileSaveFailed")}</Alert>
              ) : null}

              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  type="submit"
                  variant="contained"
                  // Nothing to save is not a thing to offer: a button that is
                  // always live invites a request that changes nothing and
                  // records an entry saying so.
                  disabled={!dirty || isSubmitting}
                >
                  {t("profile.save")}
                </Button>
                {!dirty && status !== "failed" ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("profile.upToDate")}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        </Form>
      )}
    </Formik>
  );
};
